import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import Providers from "providers";
import ReduxProvider from "providers/ReduxProvider";
import { ProductProvider, useProductContext } from "context/ProductContext";
import { FeatureFlagProvider } from "context/FeatureFlagContext";
import LayoutWithHeaderAndFooter from "layout/LayoutWithHeaderAndFooter";
import NoMatch from "layout/navigation/NoMatch";

import ScrollToTop from "components/scroll-to-top/ScrollToTop";

import InactivityHandler from "utils/inactivityHandler";
import { DTOPageTracker } from "hooks/useDTOPageTracker";
import { SaveContIdFromUrlHandler } from "hooks/useSaveContIdFromUrl";

import LandingPageLayout from "pages/landing/LandingPageLayout";
import SubmittedApplicationGuardLayout from "routes/SubmittedApplicationGuardLayout";
import InvitationCodePage from "pages/landing/InvitationCodePage";
import QuotePage from "pages/quote/QuotePage";
import MLTAQuoteResultsPage from "pages/quote/MLTAQuoteResultsPage";
import SessionExpiredPage from "pages/session-expired/SessionExpiredPage";
import SuccessDecisionPage from "pages/decision/success/SuccessDecisionPage";
import DeniedDecisionPage from "pages/decision/denied/DeniedDecisionPage";
import NeedsCloserLookPage from "pages/decision/needs-closer-look/NeedsCloserLookPage";
import CoverageLimitExceededPage from "pages/decision/coverage-limit-exceeded/CoverageLimitExceededPage";
import ReviewingApplicationPage from "pages/decision/reviewing-application/ReviewingApplicationPage";
import UnableToFinishPage from "pages/decision/unable-to-finish/UnableToFinishPage";
import WelcomeBackPage from "pages/welcome-back/WelcomeBack";
import ApplicationContainerPage from "pages/application-container/ApplicationContainerPage";
import SomethingWentWrongPage from "pages/something-went-wrong/SomethingWentWrong";
import AuthenticationFailedPage from "pages/authentication-failed/AuthenticationFailedPage";
import UncompletedApplicationExpiredPage from "pages/uncompleted-application-expired/UncompletedApplicationExpired";
import ConfirmYourIdentityPage from "pages/confirm-your-identity/ConfirmYourIdentityPage";
import ContactInformationPage from "pages/application/contact-information/ContactInformationPage";
import PaymentInformationPage from "pages/application/payment-information/PaymentInformationPage";
import ReviewInformationPage from "pages/application/review-information-page/ReviewInformationPage";

const Router = () => {
    return (
        <BrowserRouter>
            <ReduxProvider>
                <FeatureFlagProvider>
                    <ProductProvider>
                        <EverythingElse />
                    </ProductProvider>
                </FeatureFlagProvider>
            </ReduxProvider>
        </BrowserRouter>
    );
};

const EverythingElse = () => {
    const { pathname, search } = useLocation();

    return (
        <Providers pathname={pathname}>
            <ScrollToTop />
            <InactivityHandler />
            <DTOPageTracker />
            <SaveContIdFromUrlHandler />

            <Routes>{ModernProductRoutes(pathname, search)}</Routes>
        </Providers>
    );
};

/**
 * Landing Page Guard
 * GIWL applicants always start on ContactInformationPage and must never land on
 * the landing pages (invitation, quote, quote/results). For GIWL these routes
 * behave like a 404, otherwise the normal landing layout is rendered.
 */
const GIWLBlockedLandingLayout = () => {
    const { isGIWL } = useProductContext();

    if (isGIWL) {
        return <NoMatch />;
    }

    return <LandingPageLayout />;
};

/**
 * Modern Product Routes
 * Clean URL structure for all products
 * Uses routes like /invitation, /quote, /application/*, /decision/*
 */
const ModernProductRoutes = (pathname, search) => {
    return (
        <>
            {/* Backward compatibility redirect for old DTO URLs */}
            <Route
                path="/direct-mail-term/*"
                element={<Navigate to={pathname.replace("/direct-mail-term", "") + search} replace />}
            />

            <Route path="/" element={<LayoutWithHeaderAndFooter noMargins={true} />}>
                {/* Redirect root to invitation page, preserving query parameters */}
                <Route index element={<Navigate to={`/invitation${search}`} replace />} />

                {/* Landing page layout (wraps invitation and quote pages) */}
                {/* GIWL is blocked from landing pages and re-routed to a 404 (NoMatch) */}
                <Route element={<GIWLBlockedLandingLayout />}>
                    <Route path="invitation" element={<InvitationCodePage />} />
                    {/* In-flow pages are locked once the application reaches a decision page */}
                    <Route element={<SubmittedApplicationGuardLayout />}>
                        <Route path="quote" element={<QuotePage />} />
                        <Route path="quote/results" element={<MLTAQuoteResultsPage />} />
                    </Route>
                </Route>

                {/* Application routes */}
                <Route path="application" element={<ApplicationContainerPage />}>
                    <Route element={<SubmittedApplicationGuardLayout />}>
                        <Route path="contact" element={<ContactInformationPage />} />
                        <Route path="payment" element={<PaymentInformationPage />} />
                        <Route path="review" element={<ReviewInformationPage />} />
                    </Route>
                </Route>

                {/* Supporting pages */}
                <Route path="welcome-back" element={<WelcomeBackPage />} />
                <Route path="something-went-wrong" element={<SomethingWentWrongPage retryUrl="/invitation" />} />
                <Route path="session-expired" element={<SessionExpiredPage beginUrl="/invitation" />} />
                <Route path="authentication-failed" element={<AuthenticationFailedPage />} />
                <Route path="uncompleted-application-expired" element={<UncompletedApplicationExpiredPage />} />
                <Route element={<SubmittedApplicationGuardLayout />}>
                    <Route path="confirm-your-identity" element={<ConfirmYourIdentityPage />} />
                </Route>

                {/* Decision pages */}
                <Route path="decision">
                    <Route path="success" element={<SuccessDecisionPage />} />
                    <Route path="denied" element={<DeniedDecisionPage />} />
                    <Route path="needs-closer-look" element={<NeedsCloserLookPage />} />
                    <Route path="coverage-limit-exceeded" element={<CoverageLimitExceededPage />} />
                    <Route path="reviewing-application" element={<ReviewingApplicationPage />} />
                    <Route path="unable-to-finish" element={<UnableToFinishPage />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<NoMatch />} />
            </Route>
        </>
    );
};

export default Router;
